import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Dialog } from "../Dialog";
import MetadataEditor from "../Metadata/MetadataEditor";
import { ingestDataSetItem } from "../../services/data";
import {
  describeSchema,
  metadataText,
  normalizeMetadataErrors,
  parseMetadataText,
} from "../../utils/datasetMetadata";

// Ingest a data item with (optional) metadata.
//
// When the dataset declares a metadata schema the backend can generate the metadata from the
// document itself, so ingestion defaults to "Generate automatically" — the user is never forced
// to fill a form. An override editor is available, and validation/generation failures are shown
// with a retry.

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--main-border-color);
  border-radius: 8px;
  background-color: var(--main-bg);
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--main-font-color);
`;

const Modes = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ModeButton = styled.button`
  padding: 5px 11px;
  font-size: 12.5px;
  font-weight: 600;
  border: 1px solid ${(props) => (props.$active ? "var(--button-bg)" : "var(--main-border-color)")};
  border-radius: 999px;
  background-color: ${(props) => (props.$active ? "var(--sidebar-active-item-bg)" : "transparent")};
  color: var(--main-font-color);
  cursor: pointer;
`;

const Muted = styled.div`
  font-size: 12.5px;
  color: #a0a6b1;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #e74c3c;
`;

const Schema = styled.pre`
  margin: 0;
  padding: 8px 10px;
  max-height: 160px;
  overflow: auto;
  font-size: 11.5px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  background-color: var(--content-item-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  color: var(--main-font-color);
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #2ecc71;
  border: 1px solid #2ecc71;
  background-color: rgba(46, 204, 113, 0.12);
`;

/**
 * @param {object} props
 * @param {boolean} props.show
 * @param {object} props.item The row being ingested.
 * @param {object} props.dataSet
 * @param {Function} props.onClose
 * @param {(item: object) => void} props.onIngested Receives the response's `item`.
 */
export const IngestMetadataDialog = ({ show, item, dataSet, onClose = () => {}, onIngested = () => {} }) => {
  const [mode, setMode] = useState("auto");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState([]);
  const [generationFailed, setGenerationFailed] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!show) return;

    setMode("auto");
    setDraft(metadataText(item?.metadata));
    setBusy(false);
    setMessage(null);
    setErrors([]);
    setGenerationFailed(false);
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const schema = dataSet?.metadata_schema ?? dataSet?.metadataSchema ?? null;
  const hasExistingMetadata = Boolean(item?.metadata && Object.keys(item.metadata ?? {}).length > 0);

  const mediaId = item?.media_public_id ?? item?.media?.public_id ?? item?.public_id ?? null;

  const schemaText = useMemo(() => {
    if (!schema) return null;
    return typeof schema === "string" ? schema : JSON.stringify(schema, null, 2);
  }, [schema]);

  const ingest = async () => {
    if (busy || result) return;

    let value;

    if (mode === "manual") {
      const parsed = parseMetadataText(draft);

      if (!parsed.ok) {
        setMessage(`Metadata is not valid JSON: ${parsed.message}`);
        return;
      }

      const emptyObject =
        parsed.value !== null &&
        typeof parsed.value === "object" &&
        !Array.isArray(parsed.value) &&
        Object.keys(parsed.value).length === 0;

      // An empty object counts as "not supplied", like null — the backend then reuses or
      // generates metadata rather than validating an empty object.
      if (parsed.value !== null && !emptyObject) value = parsed.value;
    }

    if (!mediaId) {
      setMessage("This item has no id to ingest against.");
      return;
    }

    setBusy(true);
    setMessage(null);
    setErrors([]);
    setGenerationFailed(false);

    // `undefined` omits the field, so the backend reuses or generates the metadata.
    const response = await ingestDataSetItem(dataSet?.public_id, mediaId, value);

    setBusy(false);

    if (!response?.success) {
      setMessage(response?.message || "Failed to ingest the item.");

      if (response?.error_type === "metadata_generation") {
        setGenerationFailed(true);
      } else {
        setErrors(normalizeMetadataErrors(response));
      }

      return;
    }

    setResult(response.item ?? null);
    onIngested(response.item ?? null);
  };

  const ok = () => {
    if (result) {
      onClose();
      return;
    }
    ingest();
  };

  const okText = result ? "Done" : busy ? "Ingesting…" : generationFailed ? "Retry" : "Ingest";

  return (
    <Dialog
      title="Ingest item"
      subtitle={item?.name ?? item?.short_title ?? ""}
      show={show}
      onClose={onClose}
      onOK={ok}
      okText={okText}
      closeText={result ? "Close" : "Cancel"}
      style={{ width: "90%", maxWidth: "1000px", height: "80vh" }}
    >
      {result ? (
        <Panel>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <strong>Ingested.</strong>
            {result.metadata_source ? <Chip>{result.metadata_source}</Chip> : null}
            {result.chunk_count !== undefined ? <Muted>{result.chunk_count} chunks</Muted> : null}
          </div>

          <Muted>
            {result.metadata_source === "generated"
              ? "The metadata below was generated from the document and validated against the schema."
              : result.metadata_source === "existing"
                ? "The item's existing metadata was reused."
                : "The metadata you supplied was used."}
          </Muted>

          <Schema>{metadataText(result.metadata) || "{}"}</Schema>

          <Muted>Edit it any time from the item's Metadata column.</Muted>
        </Panel>
      ) : (
        <>
          <Panel>
            <div>
              <strong>Item metadata schema</strong>
              {schema ? <span> — {describeSchema(schema)}</span> : null}
            </div>
            {schemaText ? <Schema>{schemaText}</Schema> : <Muted>This dataset has no schema.</Muted>}
          </Panel>

          <Modes>
            <ModeButton type="button" $active={mode === "auto"} onClick={() => setMode("auto")}>
              Generate automatically
            </ModeButton>
            <ModeButton type="button" $active={mode === "manual"} onClick={() => setMode("manual")}>
              Provide metadata
            </ModeButton>
          </Modes>

          <Muted>
            <strong>Metadata:</strong>{" "}
            {mode === "manual"
              ? "Custom metadata"
              : hasExistingMetadata
                ? "Existing metadata — will be reused"
                : schema
                  ? "No metadata — will be generated"
                  : "No metadata"}
          </Muted>

          {mode === "auto" ? (
            <Muted>
              {hasExistingMetadata
                ? "This item already has metadata; it will be reused. Leave it alone to keep it."
                : schema
                  ? "Metadata will be generated automatically from the document and validated against the schema."
                  : "No metadata will be sent."}
            </Muted>
          ) : (
            <MetadataEditor value={draft} onChange={setDraft} />
          )}

          {message ? <ErrorText>{message}</ErrorText> : null}

          {errors.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "12.5px", color: "#e74c3c" }}>
              {errors.map((error, index) => (
                <li key={`${error.path}-${index}`}>
                  <code>{error.path}</code>: {error.message}
                </li>
              ))}
            </ul>
          ) : null}

          {generationFailed ? (
            <Modes>
              <ModeButton
                type="button"
                onClick={() => {
                  setGenerationFailed(false);
                  setMode("manual");
                }}
              >
                Provide metadata manually instead
              </ModeButton>
            </Modes>
          ) : null}
        </>
      )}
    </Dialog>
  );
};

export default IngestMetadataDialog;
